import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CompletionSignalMessage } from '@/types/chatCompletion';

interface ChatCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  completionData: CompletionSignalMessage['metadata'];
  onReviewSummary: () => void;
  onContinue: () => void;
  onComplete: () => void;
  isGeneratingSummary?: boolean;
}

export function ChatCompletionModal({
  isOpen,
  onClose,
  completionData,
  onReviewSummary,
  onContinue,
  onComplete,
  isGeneratingSummary = false,
}: ChatCompletionModalProps) {
  const [timeRemaining, setTimeRemaining] = useState(30);

  useEffect(() => {
    if (!isOpen || isGeneratingSummary) {
      setTimeRemaining(30);
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          onContinue(); // Auto-dismiss after 30 seconds
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, onContinue, isGeneratingSummary]);

  const getCompletionMessage = () => {
    const confidencePercent = Math.round(completionData.confidence * 100);
    
    if (completionData.reason === 'natural_completion') {
      return `Based on our conversation, it seems we've reached a natural conclusion (${confidencePercent}% confidence). Would you like to review a summary of what we discussed?`;
    } else {
      return `You've been actively engaged in this conversation. Based on your participation, it might be a good time to review what we've covered before continuing.`;
    }
  };

  const getInsightsPreview = () => {
    const { analysis } = completionData;
    if (!analysis) return null;

    return (
      <div className="mt-4 space-y-3">
        {analysis.keyInsights?.length > 0 && (
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm font-medium text-blue-900 mb-2">Key insights from our conversation:</p>
            <ul className="text-sm text-blue-800 space-y-1">
              {analysis.keyInsights.slice(0, 2).map((insight, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2 text-blue-600">•</span>
                  <span>{insight}</span>
                </li>
              ))}
              {analysis.keyInsights.length > 2 && (
                <li className="text-xs text-blue-600 italic">
                  +{analysis.keyInsights.length - 2} more insights in your summary
                </li>
              )}
            </ul>
          </div>
        )}
        
        {analysis.nextSteps?.length > 0 && (
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm font-medium text-green-900 mb-2">Recommended next steps:</p>
            <ul className="text-sm text-green-800 space-y-1">
              {analysis.nextSteps.slice(0, 2).map((step, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2 text-green-600">→</span>
                  <span>{step}</span>
                </li>
              ))}
              {analysis.nextSteps.length > 2 && (
                <li className="text-xs text-green-600 italic">
                  +{analysis.nextSteps.length - 2} more steps in your summary
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isGeneratingSummary ? 'Generating Summary...' : 'Ready to Continue?'}
          </DialogTitle>
          <DialogDescription>
            {isGeneratingSummary 
              ? 'Please wait while we create a comprehensive summary of your conversation.'
              : getCompletionMessage()
            }
          </DialogDescription>
        </DialogHeader>

        {!isGeneratingSummary && getInsightsPreview()}
        
        {isGeneratingSummary && (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Analyzing conversation and generating insights...</p>
            </div>
          </div>
        )}

        {!isGeneratingSummary && (
          <>
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    💬 {completionData.messageCount} messages
                  </span>
                  <span className="flex items-center gap-1">
                    ⏱️ {Math.round(completionData.sessionDuration / 60)} min
                  </span>
                  <span className="flex items-center gap-1">
                    🎯 {Math.round(completionData.confidence * 100)}% confidence
                  </span>
                </div>
                <span className="text-xs">Auto-dismiss in {timeRemaining}s</span>
              </div>
              
              {completionData.reason === 'engagement_threshold' && (
                <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  ✨ Detected based on your active engagement in the conversation
                </div>
              )}
              
              {completionData.reason === 'natural_completion' && (
                <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                  🎉 Natural conversation completion detected
                </div>
              )}
            </div>

            <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
              <Button
                variant="default"
                onClick={onReviewSummary}
                className="w-full sm:w-auto"
              >
                Review Summary
              </Button>
              <Button
                variant="outline"
                onClick={onContinue}
                className="w-full sm:w-auto"
              >
                Continue Chatting
              </Button>
              <Button
                variant="ghost"
                onClick={onComplete}
                className="w-full sm:w-auto"
              >
                Skip Summary
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}